/**
 * AbilityCard Component
 *
 * Displays a single dynamic ability with element icon, name, cooldown, and effects.
 */

import React from 'react';
import type { DynamicAbility } from '@drawn-of-war/shared';
import './AbilityCard.css';

// Element emoji/icon mapping
const ELEMENT_ICONS: Record<string, string> = {
  physical: '⚔️',
  fire: '🔥',
  ice: '❄️',
  lightning: '⚡',
  poison: '☠️',
  dark: '🌑',
  holy: '✨',
  arcane: '💫',
  nature: '🌿'
};

// Element color mapping
const ELEMENT_COLORS: Record<string, string> = {
  physical: '#888888',
  fire: '#FF4400',
  ice: '#00CCFF',
  lightning: '#FFFF00',
  poison: '#00FF00',
  dark: '#8800AA',
  holy: '#FFD700',
  arcane: '#AA44FF',
  nature: '#228B22'
};

// Delivery type labels
const DELIVERY_LABELS: Record<string, string> = {
  melee: 'Melee',
  projectile: 'Ranged',
  breath: 'Breath',
  beam: 'Beam',
  aoe: 'Area',
  buff: 'Support'
};

interface AbilityCardProps {
  ability: DynamicAbility;
  compact?: boolean;  // Smaller version for lists
}

export const AbilityCard: React.FC<AbilityCardProps> = ({ ability, compact = false }) => {
  const elementIcon = ELEMENT_ICONS[ability.element] || '⚪';
  const elementColor = ELEMENT_COLORS[ability.element] || '#888888';
  const deliveryLabel = DELIVERY_LABELS[ability.delivery] || ability.delivery;

  // Summarize effects
  const effectSummary = ability.effects.map(effect => {
    switch (effect.type) {
      case 'damage':
        return `${effect.value || 0} damage`;
      case 'heal':
        return `${effect.value || 0} heal`;
      case 'dot':
        return `${effect.tickDamage || 0}/s for ${effect.duration || 0}s`;
      case 'hot':
        return `${effect.tickDamage || 0} heal/s for ${effect.duration || 0}s`;
      case 'buff':
        return `+${effect.modifier || 0}% ${effect.stat}`;
      case 'debuff':
        return `${effect.modifier || 0}% ${effect.stat}`;
      case 'cc':
        return `${effect.ccType} ${effect.ccDuration || 0}s`;
      case 'movement':
        return `${effect.movementType} ${effect.distance || 0} hexes`;
      default:
        return effect.type;
    }
  }).join(', ');

  if (compact) {
    return (
      <div className="ability-card compact" style={{ borderLeftColor: elementColor }}>
        <span className="ability-icon">{elementIcon}</span>
        <span className="ability-name">{ability.name}</span>
        <span className="ability-cooldown">({ability.cooldown}s)</span>
      </div>
    );
  }

  return (
    <div className="ability-card" style={{ borderLeftColor: elementColor }}>
      <div className="ability-header">
        <span className="ability-icon">{elementIcon}</span>
        <span className="ability-name">{ability.name}</span>
        <span className="ability-delivery">{deliveryLabel}</span>
      </div>

      <div className="ability-stats">
        <span className="ability-cooldown">⏱️ {ability.cooldown}s cooldown</span>
        <span className="ability-range">📏 {ability.range.min}-{ability.range.max} range</span>
      </div>

      <div className="ability-effects">
        {effectSummary}
      </div>

      {ability.description && (
        <div className="ability-description">
          {ability.description}
        </div>
      )}
    </div>
  );
};

export default AbilityCard;
